import type { Column } from "@v1/components/designSystem/table/Table";
import type {
  ReploElementMetadata,
  ReploElementType,
} from "schemas/generated/element";

import * as React from "react";

import PageHeader from "@replo/design-system/blocks/page-header/PageHeader";
import { Separator } from "@replo/design-system/components/separator/Separator";
import twMerge from "@replo/design-system/utils/twMerge";
import DeprecatedInput from "@v1/components/common/designSystem/Input";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import SplitButton from "@v1/components/designSystem/button/SplitButton";
import SelectableChip from "@v1/components/designSystem/chip/SelectableChip";
import { DeprecatedEmptyState } from "@v1/components/designSystem/empty-state/EmptyState";
import { Menu } from "@v1/components/designSystem/menu/Menu";
import { MenuItem } from "@v1/components/designSystem/menu/MenuItem";
import Popover from "@v1/components/designSystem/popover/Popover";
import Table from "@v1/components/designSystem/table/Table";
import AnnouncementBanner from "@v1/features/announcements/AnnouncementBanner";
import { useCreateElement } from "@v1/hooks/element/useCreateElement";
import { useDuplicateElement } from "@v1/hooks/element/useDuplicateElement";
import useListAllElementsMetadata from "@v1/hooks/element/useListAllElementsMetadata";
import { useEditedTimeString } from "@v1/hooks/useEditedTimeString";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { useOpenModal } from "@v2/store/modalStore";
import { projectRouter } from "@v2/trpc/routes/project";
import { routes } from "@v2/utils/router";
import {
  AlignVerticalSpaceAround,
  ArrowDown,
  ArrowUp,
  ChevronDown,
  File,
  LayoutTemplate,
  MoreHorizontal,
  Newspaper,
  Plus,
  Search,
  ShoppingBag,
} from "lucide-react";
import { generatePath, Navigate, useNavigate } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

import PageCard from "./PageCard";

type SortBy = "name" | "createdAt" | "updatedAt";
type SortOrder = "asc" | "desc";

const sortByOptions = [
  { label: "Alphabetical", value: "name" },
  { label: "Date Created", value: "createdAt" },
  { label: "Last Modified", value: "updatedAt" },
];

const sortOrderOptions = [
  {
    label: "A to Z",
    altLabel: "Oldest First",
    value: "asc",
    startEnhancer: <ArrowUp size={14} />,
  },
  {
    label: "Z to A",
    altLabel: "Newest First",
    value: "desc",
    startEnhancer: <ArrowDown size={14} />,
  },
];

type ViewMode = "gallery" | "list";

const useFilteredPages = (
  searchTerm: string,
  selectedFilter: ReploElementType | "all",
  sortBy: SortBy,
  sortOrder: SortOrder,
) => {
  const { data: elements, isLoading } = useListAllElementsMetadata();

  if (!elements) {
    return { elements: [], isLoading };
  }

  const filteredElements = elements.filter((page: ReploElementMetadata) => {
    const nameMatch = page.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    if (selectedFilter === "all") {
      return nameMatch;
    }
    return nameMatch && selectedFilter === page.type;
  });
  const sortedElements = filteredElements.sort((a, b) => {
    let comparison = 0;
    if (sortBy === "name") {
      comparison = a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    } else if (sortBy === "createdAt" || sortBy === "updatedAt") {
      const dateA = new Date(a[sortBy]!).getTime();
      const dateB = new Date(b[sortBy]!).getTime();
      comparison = dateA - dateB;
    }

    return sortOrder === "asc" ? comparison : comparison * -1;
  });
  return { elements: sortedElements, isLoading };
};

const LastEditedDisplay: React.FC<{ updatedAt: string; createdAt: string }> = ({
  updatedAt,
  createdAt,
}) => {
  const editedTimeString = useEditedTimeString(
    new Date(updatedAt),
    new Date(createdAt),
  );
  return editedTimeString;
};

const Pages: React.FC<React.PropsWithChildren> = () => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [newPagePopoverOpen, setNewPagePopoverOpen] = React.useState(false);
  const [sortBy, setSortBy] = React.useState<SortBy>("updatedAt");
  const [sortOrder, setSortOrder] = React.useState<SortOrder>("desc");
  const [selectedFilter, setSelectedFilter] = React.useState<
    ReploElementType | "all"
  >("all");
  const { elements, isLoading } = useFilteredPages(
    searchTerm,
    selectedFilter,
    sortBy,
    sortOrder,
  );
  // NOTE (Craig 2025-06-05): Will add in a gallery mode in the future.
  // for now it will only be list mode.
  const [viewMode] = React.useState<ViewMode>("list");
  const navigate = useNavigate();
  const { project } = useCurrentProjectContext();
  const projectId = useCurrentProjectId();
  const { handleCreateElement } = useCreateElement();

  const { user } = useCurrentUser();
  const seedData = localStorage.getItem("replo_marketing_seed");

  // NOTE (Berkley 2025-09-17): If the user has been redirected to this page with seed data, they are coming from the marketing site
  // In this case, we want to redirect them to a V2 shop (if possible)
  const { data: projectsData } = projectRouter.findByUserId.useQuery(
    user?.id ?? "",
    {
      enabled: Boolean(seedData && user?.id),
    },
  );

  if (seedData && user && projectsData) {
    const reploProject = projectsData.allProjects.find(
      (proj) => proj.hostingType === "replo",
    );
    if (reploProject) {
      window.location.replace(`/shops/${reploProject.id}/chat`);
    } else {
      localStorage.removeItem("replo_marketing_seed");
    }
  }

  const handleFilterChange = (filter: ReploElementType | "all") => {
    if (selectedFilter === filter) {
      setSelectedFilter("all");
    } else {
      setSelectedFilter(filter);
    }
  };

  const pageTableColumns: Column<ReploElementMetadata & { key: string }>[] = [
    {
      key: "name",
      header: "Name",
      renderCell: (element) => (
        <div className="flex items-center gap-x-2">
          {element.isPublished && (
            <div className="w-2.5 h-2.5 bg-emerald rounded-full shrink-0" />
          )}
          <div className="text-default cursor-pointer truncate font-medium">
            {element.name}
          </div>
        </div>
      ),
    },
    {
      key: "lastEdited",
      header: "Last Edited",
      renderCell: (element) => (
        <LastEditedDisplay
          updatedAt={element.updatedAt}
          createdAt={element.createdAt}
        />
      ),
    },
    {
      key: "type",
      header: "Type",
      renderCell: (element) => {
        const {
          icon,
          className: iconClassName,
          label,
        } = exhaustiveSwitch({
          type: element.type,
        })<{
          icon: React.JSX.Element;
          className: string;
          label: string;
        }>({
          page: () => ({
            label: "Page",
            icon: <File size={12} />,
            className: "text-primary bg-sky-soft",
          }),
          shopifyProductTemplate: () => ({
            label: "Product Template",
            icon: <ShoppingBag size={12} />,
            className: "text-blue-600 bg-blue-50",
          }),
          shopifySection: () => ({
            label: "Section",
            icon: <AlignVerticalSpaceAround size={12} />,
            className: "textfuchsia-600 bg-fuchsia-50",
          }),
          shopifyArticle: () => ({
            label: "Blog",
            icon: <Newspaper size={12} />,
            className: "text-pink-600 bg-pink-50",
          }),
        });
        return (
          <div className="flex gap-2 items-center">
            <div
              className={twMerge(
                "h-5 w-5 flex items-center justify-center rounded-md",
                iconClassName,
              )}
            >
              {icon}
            </div>
            {label}
          </div>
        );
      },
    },
    {
      key: "actions",
      header: "",
      renderCell: (element) => (
        <PageMenu element={element} elements={elements} />
      ),
      align: "right",
    },
  ];

  if (project?.hostingType === "replo") {
    return <Navigate to={`/shops/${projectId}/home`} />;
  }

  return (
    <>
      <PageHeader
        title="Pages"
        actions={
          <SplitButton
            buttonText="New Page"
            buttonId="new-page"
            size="base"
            variant="primary"
            startEnhancer={<Plus size={14} />}
            onButtonClick={() => {
              // NOTE (Gabe 2025-08-19): We use window.location.href instead of
              // navigate because we want to force a full page reload, which
              // is necessary to ensure that the COOP/COEP headers are unset.
              window.location.href = `/editor/${projectId}/new`;
            }}
            popoverProps={{
              isOpen: newPagePopoverOpen,
              onOpenChange: (isOpen: boolean) => setNewPagePopoverOpen(isOpen),
              content: (
                <div className="flex flex-col gap-1 min-w-0">
                  <MenuItem
                    variant="default"
                    size="sm"
                    startEnhancer={<File size={12} />}
                    onClick={() => {
                      void handleCreateElement({ type: "page" });
                      setNewPagePopoverOpen(false);
                    }}
                  >
                    Blank Page
                  </MenuItem>
                  <MenuItem
                    variant="default"
                    size="sm"
                    startEnhancer={<LayoutTemplate size={12} />}
                    onClick={() => {
                      navigate(`/editor/${projectId}/new`);
                    }}
                  >
                    Choose Template
                  </MenuItem>
                </div>
              ),
              className: "w-[160px] p-1.5",
              skipTriggerStyles: true,
              hideCloseButton: true,
              sideOffset: 4,
            }}
          />
        }
      >
        <AnnouncementBanner tags={["New"]} />
      </PageHeader>
      <div className="flex w-full overflow-hidden min-h-0 flex-col gap-y-6 py-6">
        <DeprecatedInput
          placeholder="Search"
          startEnhancer={<Search size={14} />}
          layoutClassName="w-full shrink-0"
          size="base"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="flex items-center flex-shrink-0 gap-x-2">
          <SelectableChip
            size="base"
            onClick={() => handleFilterChange("all")}
            active={selectedFilter === "all"}
          >
            All
          </SelectableChip>
          <SelectableChip
            size="base"
            UNSAFE_activeClassName="text-primary bg-sky-soft border-primary hover:bg-primary-hover"
            startEnhancer={<File size={12} />}
            onClick={() => handleFilterChange("page")}
            active={selectedFilter === "page"}
          >
            Pages
          </SelectableChip>
          <SelectableChip
            size="base"
            UNSAFE_activeClassName="text-blue-600 bg-blue-50 border-blue-600 hover:bg-blue-100"
            startEnhancer={<ShoppingBag size={12} />}
            onClick={() => handleFilterChange("shopifyProductTemplate")}
            active={selectedFilter === "shopifyProductTemplate"}
          >
            Product Templates
          </SelectableChip>
          <SelectableChip
            size="base"
            UNSAFE_activeClassName="text-fuchsia-600 bg-fuchsia-50 border-fuchsia-600 hover:bg-fuchsia-100"
            startEnhancer={<AlignVerticalSpaceAround size={12} />}
            onClick={() => handleFilterChange("shopifySection")}
            active={selectedFilter === "shopifySection"}
          >
            Sections
          </SelectableChip>
          <SelectableChip
            size="base"
            UNSAFE_activeClassName="text-pink-600 bg-pink-50 border-pink-600 hover:bg-pink-100"
            startEnhancer={<Newspaper size={12} />}
            onClick={() => handleFilterChange("shopifyArticle")}
            active={selectedFilter === "shopifyArticle"}
          >
            Blogs
          </SelectableChip>
          <div className="flex ml-auto gap-x-2">
            <SortMenu
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
            />
          </div>
        </div>
        {viewMode === "gallery" ? (
          <div className="grid grid-cols-[repeat(auto-fill,270px)] gap-6 justify-items-start">
            {elements.map((page: ReploElementMetadata) => (
              <PageCard
                key={page.id}
                pageTitle={page.name}
                lastEditedAt={page.updatedAt}
                type={page.type as ReploElementType}
                isPublished={page.isPublished ?? false}
                createdAt={page.createdAt}
                onClick={() => {
                  return navigate(
                    generatePath(routes.editor.element, {
                      projectId: page.projectId,
                      elementId: page.id,
                    }),
                  );
                }}
              />
            ))}
          </div>
        ) : (
          <Table
            data={elements.map((element) => ({ ...element, key: element.id }))}
            columns={pageTableColumns}
            isLoading={isLoading}
            variant="bordered"
            layoutClassName="flex-1"
            scrollable={true}
            emptyState={
              <DeprecatedEmptyState
                title="No pages found"
                description="Create a new page to get started."
                size="base"
                variant="primary"
                icon={<File size={16} />}
                UNSAFE_className="py-[15%]"
              >
                <DeprecatedButton
                  variant="link"
                  size="base"
                  onClick={() => navigate(`/editor/${projectId}/new`)}
                >
                  Create New Page
                </DeprecatedButton>
              </DeprecatedEmptyState>
            }
            onRowClick={(element) => {
              navigate(
                generatePath(routes.editor.element, {
                  projectId: element.projectId,
                  elementId: element.id,
                }),
              );
            }}
          />
        )}
      </div>
    </>
  );
};

const SortMenu: React.FC<{
  sortBy: SortBy;
  setSortBy: (sortBy: SortBy) => void;
  sortOrder: SortOrder;
  setSortOrder: (sortOrder: SortOrder) => void;
}> = ({ sortBy, setSortBy, sortOrder, setSortOrder }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const selectedSortByOption = sortByOptions.find(
    (option) => option.value === sortBy,
  );
  const selectedSortOrderOption = sortOrderOptions.find(
    (option) => option.value === sortOrder,
  );

  return (
    <Popover
      side="bottom"
      shouldPreventDefaultOnInteractOutside={false}
      hideCloseButton
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      align="end"
      triggerAsChild
      content={
        <div className="text-default flex w-full flex-col gap-2 text-xs">
          <div className="flex flex-col gap-1">
            <h3 className="text-xs font-semibold px-2">Sort By</h3>
            {sortByOptions.map((option) => (
              <MenuItem
                key={option.value}
                variant="default"
                value={option.value}
                selected={sortBy === option.value}
                onClick={() => setSortBy(option.value as SortBy)}
                size="sm"
              >
                {option.label}
              </MenuItem>
            ))}
          </div>
          <Separator />
          <div className="flex flex-col gap-1">
            <h3 className="text-xs font-semibold px-2">Sort Order</h3>
            {sortOrderOptions.map((option) => (
              <MenuItem
                key={option.value}
                variant="default"
                value={option.value}
                selected={sortOrder === option.value}
                onClick={() => setSortOrder(option.value as SortOrder)}
                size="sm"
              >
                {sortBy === "name" ? option.label : option.altLabel}
              </MenuItem>
            ))}
          </div>
        </div>
      }
    >
      <DeprecatedButton
        variant="secondary"
        size="base"
        endEnhancer={<ChevronDown size={14} />}
        startEnhancer={selectedSortOrderOption?.startEnhancer}
      >
        {selectedSortByOption?.label}
      </DeprecatedButton>
    </Popover>
  );
};

const PageMenu = ({
  element,
  elements,
}: {
  element: ReploElementMetadata;
  elements: ReploElementMetadata[];
}) => {
  const { handleDuplicateElement } = useDuplicateElement();
  const openModal = useOpenModal();
  const elementTypeSingularName = exhaustiveSwitch({ type: element.type })({
    page: "Page",
    shopifyArticle: "Post",
    shopifyProductTemplate: "Template",
    shopifySection: "Section",
  });
  return (
    <Menu
      items={[
        {
          id: "duplicate",
          type: "leaf",
          variant: "default",
          size: "sm",
          title: `Duplicate ${elementTypeSingularName}`,
          onSelect: () => void handleDuplicateElement(element),
        },
        {
          id: "delete",
          type: "leaf",
          variant: "default",
          size: "sm",
          title: `Delete ${elementTypeSingularName}`,
          onSelect: () => {
            openModal({
              type: "deleteElementConfirmationModal",
              props: {
                type: "delete",
                element,
                elements,
              },
            });
          },
        },
      ]}
      align="end"
      trigger={
        <DeprecatedIconButton
          variant="tertiary"
          size="sm"
          icon={<MoreHorizontal size={16} className="text-muted" />}
        />
      }
    />
  );
};

export default Pages;
